import { useEffect, useState } from 'react';
import { Stack, Switch } from '@mui/material';
import { WarningRounded } from '@mui/icons-material';
import { useFormik } from 'formik';
import { TAdminInternetResource } from '~/store/internetResource/internetResource.type';
import {
  useInternetDropDownListQuery,
  useUpdateInternetByIdMutation,
} from '~/api/oneBoxV3';
import {
  TUniqInternetResourceProps,
  internetCategory,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { defaultOption } from '~/utils/constants';
import { pxToRem } from '~/theme/typography';
import { admin } from '~/components/Admin/AdminDialog/AdminDialog.style';
import { AdminDialogActions } from '~/components/Admin/AdminDialog/AdminDialogActions/AdminDialogActions';
import { ACTION_STATUS } from '~/components/Admin/ResourceTable';
import { defaultInternetEditResourceForm } from '~/components/Admin/AdminComponent.constant';
import { THandleEditInternetResourceForm } from '~/components/Admin/AdminComponent.type';
import { StyledTextField } from '~/components/Admin/ResourceFilter/ResourceFilter.style';
import { FormSchema } from '~/utils/validation';
import { InternetEditDialogAutocomplete } from './InternetEditDialogAutocomplete';

type TProps = {
  editedResource: TAdminInternetResource;
  handleClose: () => void;
};
const labelProps = (label: string) => ({
  label: <admin.InfoTitle>{label}</admin.InfoTitle>,
  labelPlacement: 'start' as const,
});

const downloadListPayload = {
  categories: [internetCategory.SCHOOL, internetCategory.PUBLIC],
  internetResourceProps: [
    'years',
    'eduCodes',
    'subjects',
    'grades',
    'resources1',
    'resources2',
  ],
};

export const InternetEditDialogContent = ({
  editedResource,
  handleClose,
}: TProps) => {
  const [dropDownList, setDropDownList] =
    useState<TUniqInternetResourceProps>(defaultOption);
  const { data: dropDownListApiData } =
    useInternetDropDownListQuery(downloadListPayload);
  const [updateInternetByIdApi] = useUpdateInternetByIdMutation();

  const formik = useFormik({
    initialValues: defaultInternetEditResourceForm,
    validationSchema: FormSchema,
    onSubmit: async values => {
      const newValues = { ...values, category: values.category[0] };
      await updateInternetByIdApi(newValues).unwrap();
      handleClose();
    },
  });
  const { values, errors, touched, isSubmitting, setValues, setFieldValue } =
    formik;
  const errorProps = (key: keyof typeof formik.values) => ({
    error: touched[key] && Boolean(errors[key]),
    helperText: touched[key] && errors[key] && (
      <Stack direction="row" alignItems="center">
        <WarningRounded sx={{ fontSize: '16px' }} />
        {errors[key]}
      </Stack>
    ),
  });

  const handleEdit = ({ newValue, id }: THandleEditInternetResourceForm) => {
    void setFieldValue(id, newValue.length > 0 ? newValue : []);
  };

  useEffect(() => {
    if (!dropDownListApiData || !dropDownListApiData.data) return;
    const data = dropDownListApiData.data.uniqInternetResourceProps;
    setDropDownList(prev => ({ ...prev, ...data }));
  }, [dropDownListApiData]);

  useEffect(() => {
    if (!editedResource) return;
    void setValues({
      _id: editedResource._id,
      category: [editedResource.category],
      years: editedResource.years,
      eduCodes: editedResource.eduCodes,
      subjects: editedResource.subjects,
      grades: editedResource.grades,
      resources1: editedResource.resources1,
      resources2: editedResource.resources2,
      urlName: editedResource.urlName,
      url: editedResource.url,
      isLaunch: editedResource.isLaunch,
    });
  }, [editedResource]);

  return (
    <>
      <admin.DialogContent>
        <admin.ContentContainer>
          <admin.ContentTitle>檔案設定</admin.ContentTitle>
          <admin.ContentSubContainer>
            <admin.FormControlLabel
              control={
                <>
                  <admin.InfoContent
                    sx={{ marginLeft: '-16px', fontSize: pxToRem(14) }}
                  >
                    {values.isLaunch ? '上架中' : '已下架'}
                  </admin.InfoContent>
                  <Switch
                    checked={values.isLaunch}
                    color="info"
                    onChange={formik.handleChange}
                    id="isLaunch"
                  />
                </>
              }
              {...labelProps('狀態*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="category"
                  customOptions={[
                    internetCategory.SCHOOL,
                    internetCategory.PUBLIC,
                  ]}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('類別*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="years"
                  customOptions={dropDownList.years ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('學年度*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="eduCodes"
                  customOptions={dropDownList.eduCodes ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('學制*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="subjects"
                  customOptions={dropDownList.subjects ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('科目*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="grades"
                  customOptions={dropDownList.grades ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('年級*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="resources1"
                  customOptions={dropDownList.resources1 ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('資源類別１*')}
            />
            <admin.FormControlLabel
              control={
                <InternetEditDialogAutocomplete
                  id="resources2"
                  customOptions={dropDownList.resources2 ?? []}
                  values={values}
                  handleEdit={handleEdit}
                  errorProps={errorProps}
                />
              }
              {...labelProps('資源類別２*')}
            />
            <admin.FormControlLabel
              control={
                <StyledTextField
                  size="small"
                  fullWidth
                  value={values['urlName']}
                  name="urlName"
                  onChange={formik.handleChange}
                  {...errorProps('urlName')}
                />
              }
              {...labelProps('資源名稱*')}
            />
            <admin.FormControlLabel
              control={
                <StyledTextField
                  size="small"
                  fullWidth
                  value={values['url']}
                  name="url"
                  onChange={formik.handleChange}
                  {...errorProps('url')}
                />
              }
              {...labelProps('URL*')}
            />
          </admin.ContentSubContainer>
        </admin.ContentContainer>
      </admin.DialogContent>
      <AdminDialogActions
        handleClose={handleClose}
        handleSubmit={() => formik.handleSubmit()}
        status={ACTION_STATUS.EDIT}
        acceptButtonText="儲存變更"
        disabled={isSubmitting}
      />
    </>
  );
};
